import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { LoginForm } from '../../../../components/feature/LoginForm';
import AuthShell from '../components/AuthShell';

const REDIRECT_URL_KEY = 'auth_redirect_url';

/**
 * Notes the sign-in panel closes with.
 *
 * Only statements the application actually backs: access is restricted to
 * issued accounts, and sign-in attempts are written to the audit trail. No
 * certification marks or compliance badges — a government login page must not
 * assert an accreditation it does not hold.
 */
const ASSURANCES = [
  { icon: 'ri-shield-keyhole-line', label: 'Authorised users only' },
  { icon: 'ri-file-list-3-line', label: 'Sign-in activity is recorded' },
];

const Login: React.FC = () => {
  const { isAuthenticated, pendingUdaSelection, isSelectingUda } = useAuth();
  const navigate = useNavigate();
  // Note: ?redirect= parameter is ignored to prevent redirecting to previous user's URLs

  // A multi-UDA employee is authenticated the moment the password checks out, but the
  // session is not yet bound to a UDA. Hold this page until they answer — the dialog is
  // meant to appear OVER the sign-in page, and navigating away would leave it floating
  // above a dashboard belonging to whichever UDA the token happened to default to.
  const awaitingUdaChoice = !!pendingUdaSelection || isSelectingUda;

  useEffect(() => {
    if (isAuthenticated && !awaitingUdaChoice) {
      const storedRedirectPath = localStorage.getItem('landing_page_url');
      const preservedRedirectPath = localStorage.getItem(REDIRECT_URL_KEY);

      // Priority: preserved redirect > landing page > home
      // Note: ?redirect= parameter is ignored to prevent redirecting to previous user's URLs
      const targetPath = preservedRedirectPath || storedRedirectPath || '/';

      // Clear the preserved URL after using it
      if (preservedRedirectPath) {
        localStorage.removeItem(REDIRECT_URL_KEY);
      }

      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, awaitingUdaChoice, navigate]);

  // Keep the sign-in page rendered underneath while the UDA dialog is open.
  if (isAuthenticated && !awaitingUdaChoice) {
    return <LoadingSpinner />;
  }

  return (
    <AuthShell bandLabel="DTCP ERP · Sign in">
      {/* ── Panel header (a div, not a <header>: see the note in AuthShell) ──
          Left-aligned and deliberately compact. The shell already carries the
          full Government of Andhra Pradesh / Directorate lockup on both the
          desktop hero and the mobile masthead, so repeating it at heading size
          here would crowd the form rather than reinforce it. */}
      <div className="mb-5">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-400">
          Government of Andhra Pradesh
        </p>
        <h2 className="mt-1.5 text-[22px] font-bold leading-tight tracking-tight text-[#0a1e3f] dark:text-white">
          Sign in to DTCP&nbsp;ERP
        </h2>
        <div
          className="mt-2.5 h-[3px] w-12 rounded-full bg-gradient-to-r from-[#d4a017] to-[#e8be55]"
          aria-hidden="true"
        />
        {/* One line, not two. The directorate's name is already on the plate in
            every layout, and a second line here pushed the sign-in button below
            the fold on a 768px laptop. */}
        <p className="mt-2.5 text-[13px] leading-relaxed text-slate-600 dark:text-gray-400">
          Enter your{' '}
          <span className="font-semibold text-slate-800 dark:text-gray-200">current ERP login</span>{' '}
          credentials to continue.
        </p>
      </div>

      <LoginForm colorScheme="sunset" autoFocus={true} useERPAuth={true} />

      {/* ── Assurances ──
          A single unruled line rather than a bordered block: it costs the card
          about 45px of height, which is the difference between the button
          sitting above or below the fold on a short laptop. */}
      <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {ASSURANCES.map((item) => (
          <li
            key={item.label}
            className="flex items-center gap-1.5 text-[11.5px] text-slate-500 dark:text-gray-500"
          >
            <i className={`${item.icon} text-[13px] text-slate-400`} aria-hidden="true" />
            {item.label}
          </li>
        ))}
      </ul>
    </AuthShell>
  );
};

export default Login;
